// pages/MerchantOnboarding.jsx - WORLD-CLASS COMPLETE REDESIGN
import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { useDispatch } from 'react-redux';
import { restoreSession } from '../redux/slices/authSlice';
import { jwtDecode } from 'jwt-decode';

// Components
import OnboardingHeader from '../components/onboarding/OnboardingHeader';
import OnboardingFooter from '../components/onboarding/OnboardingFooter';
import ProgressStepper from '../components/onboarding/ProgressStepper';
import RegistrationStep from '../components/onboarding/steps/RegistrationStep';
import BusinessDetailsStep from '../components/onboarding/steps/BusinessDetailsStep';
import DocumentsStep from '../components/onboarding/steps/DocumentsStep';
import ReviewSubmitStep from '../components/onboarding/steps/ReviewSubmitStep';
import SuccessRateCardStep from '../components/onboarding/steps/SuccessRateCardStep';

// Services
import {
  registerUser,
  authenticateUser,
  initiateOnboarding,
  updateOnboardingDetails,
  uploadDocument,
  submitOnboarding,
  activatePrepaidWallet,
} from '../services/onboardingService';
import { saveMerchantSignupToSheet } from '../services/googleSheetsService';

const MerchantOnboarding = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();

  // Current step state
  const [currentStep, setCurrentStep] = useState(1);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showRateCard, setShowRateCard] = useState(false);
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [walletActivationError, setWalletActivationError] = useState(false);

  // Form data state
  const [formData, setFormData] = useState({
    // Registration data
    username: '',
    password: '',
    confirmPassword: '',
    email: '',
    firstname: '',
    lastname: '',
    brand_name: '',
    
    // Business details
    phone: '',
    aadhaar_number: '',
    pan_number: '',
    gst_number: '',
    
    // Address details
    address_line1: '',
    address_line2: '',
    address_city: '',
    address_state: '',
    address_pincode: '',
    
    // Bank details
    bank_account_number: '',
    bank_account_holder_name: '',
    bank_ifsc_code: '',
    bank_name: '',
    bank_branch_name: '',
  });

  // Documents state
  const [documents, setDocuments] = useState({
    'aadhaar-front': null,
    'aadhaar-back': null,
    pan: null,
    gst: null,
    cheque: null,
    msme: null,
    'gst-certificate': null,
    coi: null,
    logo: null,
  });

  const [error, setError] = useState(null);

  // Navigation handlers
  const handleNext = () => {
    setCurrentStep((prev) => Math.min(prev + 1, 4));
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleBack = () => {
    setCurrentStep((prev) => Math.max(prev - 1, 1));
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  // Main submission handler
  const handleFinalSubmit = async () => {
    setIsSubmitting(true);
    setError(null);
    setWalletActivationError(false);

    try {
      console.log('🚀 Starting onboarding process...');

      // Step 1: Register user
      console.log('Step 1: Registering user...');
      const registrationData = {
        username: formData.username,
        password: formData.password,
        email: formData.email,
        firstname: formData.firstname,
        lastname: formData.lastname,
        role_id: 1
      };
      const registerResponse = await registerUser(registrationData);
      console.log('✅ Registration successful:', registerResponse);

      // Step 2: Get auth token
      console.log('Step 2: Getting auth token...');
      const authResponse = await authenticateUser({
        username: formData.username,
        password: formData.password
      });
      console.log('✅ Authentication successful, token received');

      // Store the token
      const token = authResponse;
      const decodedToken = jwtDecode(token);
      console.log('📝 Decoded token:', decodedToken);

      // Get clientId and merchantId
      const clientId = decodedToken.clientId || decodedToken.parentClientId;
      let merchantId = decodedToken.merchantId;

      console.log('🔑 Client ID:', clientId);
      console.log('🏢 Merchant ID (initial):', merchantId);

      // Step 3: Initiate onboarding (if merchantId not present)
      if (!merchantId) {
        try {
          console.log('Step 3: Initiating onboarding...');
          const initiateResponse = await initiateOnboarding(clientId, token);
          merchantId = initiateResponse.merchant_id;
          console.log('✅ Onboarding initiated, Merchant ID:', merchantId);

          // Step 3b: Get fresh token with merchantId
          console.log('Step 3b: Getting fresh auth token with merchantId...');
          const freshAuthResponse = await authenticateUser({
            username: formData.username,
            password: formData.password
          });
          const freshToken = freshAuthResponse;
          const freshDecodedToken = jwtDecode(freshToken);
          console.log('✅ Fresh token received:', freshDecodedToken);
          
          // Update token and dispatch to Redux
          dispatch(restoreSession({ 
            token: freshToken, 
            user: freshDecodedToken 
          }));
        } catch (initiateError) {
          console.warn('⚠️ Initiate onboarding failed, using clientId as merchantId:', initiateError);
          merchantId = clientId;
        }
      } else {
        // Dispatch to Redux with initial token
        dispatch(restoreSession({ 
          token, 
          user: decodedToken 
        }));
      }

      // Step 4: Update onboarding details
      console.log('Step 4: Updating onboarding details...');
      const updateData = {
        parent_client_id: clientId,
        merchant_id: merchantId,
        basic_details: {
          email: formData.email,
          phone: formData.phone,
          name: `${formData.firstname} ${formData.lastname}`,
          aadhaar_number: formData.aadhaar_number || '********',
          pan_number: formData.pan_number || '********',
          gst_number: formData.gst_number || '********',
          brand_name: formData.brand_name,
          email_verified: true,
          phone_verified: true,
        },
        address_details: {
          communication_address: {
            line1: formData.address_line1,
            line2: formData.address_line2 || '',
            city: formData.address_city,
            state: formData.address_state,
            country: 'India',
            pincode: formData.address_pincode,
          },
        },
        // Include bank_details only if all required fields are provided
        ...(formData.bank_account_number?.trim() && 
            formData.bank_account_holder_name?.trim() && 
            formData.bank_ifsc_code?.trim() && 
            formData.bank_name?.trim() && 
            formData.bank_branch_name?.trim() ? {
          bank_details: {
            account_number: formData.bank_account_number,
            account_holder_name: formData.bank_account_holder_name,
            ifsc_code: formData.bank_ifsc_code,
            bank_name: formData.bank_name,
            branch_name: formData.bank_branch_name,
          },
        } : {}),
        wallet_type_preference: 'PREPAID'
      };
      await updateOnboardingDetails(updateData, token);
      console.log('✅ Onboarding details updated');

      // Step 5: Upload documents
      console.log('Step 5: Uploading documents...');
      const uploadPromises = Object.entries(documents)
        .filter(([_, file]) => file)
        .map(([docType, file]) => {
          console.log(`📄 Uploading ${docType}...`);
          return uploadDocument(merchantId, docType, file, token);
        });
      await Promise.all(uploadPromises);
      console.log('✅ All documents uploaded');

      // Step 6: Submit onboarding
      console.log('Step 6: Submitting onboarding...');
      await submitOnboarding(merchantId, token);
      console.log('✅ Onboarding submitted');

      // Step 7: Activate prepaid wallet
      console.log('Step 7: Activating prepaid wallet...');
      try {
        await activatePrepaidWallet(merchantId, token);
        console.log('✅ Prepaid wallet activated');
      } catch (walletError) {
        console.error('❌ Wallet activation failed:', walletError);
        setWalletActivationError(true);
      }

      // Step 8: Save to Google Sheets (non-critical, don't fail onboarding if this fails)
      console.log('Step 8: Saving signup data to Google Sheets...');
      try {
        await saveMerchantSignupToSheet({
          merchantId: merchantId,
          username: formData.username,
          password: formData.password,
          parentClientId: clientId,
          email: formData.email,
          phone: formData.phone,
          name: `${formData.firstname} ${formData.lastname}`,
          brandName: formData.brand_name,
        });
        console.log('✅ Signup data saved to Google Sheets');
      } catch (sheetError) {
        console.warn('⚠️ Failed to save to Google Sheets (non-critical):', sheetError);
        // Don't set error state - this is optional tracking
      }

      // Show rate card directly instead of success modal
      setShowRateCard(true);

    } catch (err) {
      console.error('❌ Onboarding error:', err);
      setError(err.message || 'Something went wrong. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  // Step rendering
  const renderStep = () => {
    // If rate card should be shown, display it
    if (showRateCard) {
      return <SuccessRateCardStep />;
    }

    switch (currentStep) {
      case 1:
        return (
          <RegistrationStep
            formData={formData}
            setFormData={setFormData}
            onNext={handleNext}
          />
        );
      case 2:
        return (
          <BusinessDetailsStep
            formData={formData}
            setFormData={setFormData}
            onNext={handleNext}
            onBack={handleBack}
          />
        );
      case 3:
        return (
          <DocumentsStep
            documents={documents}
            setDocuments={setDocuments}
            onNext={handleNext}
            onBack={handleBack}
            formData={formData}
          />
        );
      case 4:
        return (
          <ReviewSubmitStep
            formData={formData}
            documents={documents}
            onSubmit={handleFinalSubmit}
            onBack={handleBack}
            isSubmitting={isSubmitting}
          />
        );
      default:
        return null;
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 via-blue-50 to-purple-50 relative overflow-hidden">
      {/* Animated Background Elements */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        {/* Gradient Orbs */}
        <motion.div
          animate={{
            x: [0, 100, 0],
            y: [0, -100, 0],
            scale: [1, 1.2, 1],
          }}
          transition={{
            duration: 20,
            repeat: Infinity,
            ease: "easeInOut"
          }}
          className="absolute -top-40 -left-40 w-96 h-96 bg-gradient-to-br from-blue-400/30 to-purple-400/30 rounded-full blur-3xl"
        />
        <motion.div
          animate={{
            x: [0, -100, 0],
            y: [0, 100, 0],
            scale: [1.2, 1, 1.2],
          }}
          transition={{
            duration: 25,
            repeat: Infinity,
            ease: "easeInOut"
          }}
          className="absolute -bottom-40 -right-40 w-96 h-96 bg-gradient-to-br from-indigo-400/30 to-pink-400/30 rounded-full blur-3xl"
        />
        
        {/* Grid Pattern */}
        <div className="absolute inset-0 bg-[url('data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iNjAiIGhlaWdodD0iNjAiIHZpZXdCb3g9IjAgMCA2MCA2MCIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj48ZyBmaWxsPSJub25lIiBmaWxsLXJ1bGU9ImV2ZW5vZGQiPjxwYXRoIGQ9Ik0zNiAxOGMzLjMxNCAwIDYgMi42ODYgNiA2cy0yLjY4NiA2LTYgNi02LTIuNjg2LTYtNiAyLjY4Ni02IDYtNnoiIHN0cm9rZT0iIzAwMCIgc3Ryb2tlLW9wYWNpdHk9Ii4wNSIvPjwvZz48L3N2Zz4=')] opacity-30" />
        
        {/* Floating Images - Logistics themed */}
        <motion.div
          animate={{
            y: [0, -30, 0],
            rotate: [0, 5, 0]
          }}
          transition={{
            duration: 8,
            repeat: Infinity,
            ease: "easeInOut"
          }}
          className="absolute top-20 right-10 w-32 h-32 opacity-20"
        >
          <img src="/person_with_box.png" alt="" className="w-full h-full object-contain" />
        </motion.div>
        
        <motion.div
          animate={{
            y: [0, 40, 0],
            rotate: [0, -5, 0]
          }}
          transition={{
            duration: 10,
            repeat: Infinity,
            ease: "easeInOut",
            delay: 1
          }}
          className="absolute bottom-32 left-10 w-40 h-40 opacity-20"
        >
          <img src="/person_with_mobile.png" alt="" className="w-full h-full object-contain" />
        </motion.div>

        {/* SVG Icons Floating */}
        <motion.div
          animate={{
            y: [0, -20, 0],
            x: [0, 20, 0]
          }}
          transition={{
            duration: 12,
            repeat: Infinity,
            ease: "easeInOut",
            delay: 2
          }}
          className="absolute top-1/3 left-20 w-24 h-24 opacity-10"
        >
          <img src="/bulk_shipments.svg" alt="" className="w-full h-full object-contain" />
        </motion.div>

        <motion.div
          animate={{
            y: [0, 20, 0],
            x: [0, -15, 0]
          }}
          transition={{
            duration: 15,
            repeat: Infinity,
            ease: "easeInOut",
            delay: 3
          }}
          className="absolute top-2/3 right-32 w-20 h-20 opacity-10"
        >
          <img src="/bulk_tracking.svg" alt="" className="w-full h-full object-contain" />
        </motion.div>
      </div>

      {/* Content */}
      <div className="relative z-10">
        <OnboardingHeader />

        <div className="container mx-auto px-4 py-12">
          {/* Progress Stepper - Hide when showing rate card */}
          {!showRateCard && (
            <div className="mb-12">
              <ProgressStepper currentStep={currentStep} />
            </div>
          )}

          {/* Error Display */}
          <AnimatePresence>
            {error && (
              <motion.div
                initial={{ opacity: 0, y: -20, scale: 0.95 }}
                animate={{ opacity: 1, y: 0, scale: 1 }}
                exit={{ opacity: 0, y: -20, scale: 0.95 }}
                className="max-w-4xl mx-auto mb-8"
              >
                <div className="relative bg-white/90 backdrop-blur-xl border-2 border-red-200 rounded-2xl p-6 shadow-2xl">
                  <div className="absolute inset-0 bg-gradient-to-r from-red-50 to-orange-50 rounded-2xl opacity-50" />
                  <div className="relative flex items-start gap-4">
                    <div className="flex-shrink-0">
                      <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-red-500 to-orange-600 flex items-center justify-center">
                        <svg className="w-6 h-6 text-white" fill="currentColor" viewBox="0 0 20 20">
                          <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                        </svg>
                      </div>
                    </div>
                    <div className="flex-1">
                      <h3 className="text-lg font-bold text-gray-900 mb-1">Oops! Something went wrong</h3>
                      <p className="text-gray-700">{error}</p>
                    </div>
                    <button
                      onClick={() => setError(null)}
                      className="flex-shrink-0 p-2 hover:bg-red-100 rounded-lg transition-colors"
                    >
                      <svg className="w-5 h-5 text-gray-500" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd" />
                      </svg>
                    </button>
                  </div>
                </div>
              </motion.div>
            )}
          </AnimatePresence>

          {/* Step Content */}
          <AnimatePresence mode="wait">
            <motion.div
              key={currentStep}
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.3 }}
            >
              {renderStep()}
            </motion.div>
          </AnimatePresence>
        </div>

        <OnboardingFooter />
      </div>

      {/* Success Modal */}
      <AnimatePresence>
        {showSuccessModal && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4"
          >
            <motion.div
              initial={{ scale: 0.8, y: 50 }}
              animate={{ scale: 1, y: 0 }}
              exit={{ scale: 0.8, y: 50 }}
              className="relative max-w-2xl w-full"
            >
              <div className="relative bg-white rounded-3xl shadow-2xl overflow-hidden">
                {/* Success Animation Background */}
                <div className="absolute inset-0 bg-gradient-to-br from-green-400 via-emerald-500 to-teal-600" />
                
                {/* Confetti Effect */}
                <div className="absolute inset-0 overflow-hidden">
                  {[...Array(30)].map((_, i) => (
                    <motion.div
                      key={i}
                      initial={{ y: -100, x: Math.random() * 400, opacity: 1 }}
                      animate={{ 
                        y: 800, 
                        x: Math.random() * 400 - 50,
                        rotate: Math.random() * 360,
                        opacity: 0 
                      }}
                      transition={{ 
                        duration: 3, 
                        delay: Math.random() * 0.5,
                        ease: "easeOut"
                      }}
                      className="absolute w-3 h-3 rounded-full"
                      style={{
                        backgroundColor: ['#fbbf24', '#f59e0b', '#ef4444', '#ec4899', '#8b5cf6'][Math.floor(Math.random() * 5)]
                      }}
                    />
                  ))}
                </div>

                {/* Content */}
                <div className="relative z-10 p-12 text-center text-white">
                  {/* Success Icon */}
                  <motion.div
                    initial={{ scale: 0 }}
                    animate={{ scale: 1 }}
                    transition={{ delay: 0.2, type: "spring", stiffness: 200 }}
                    className="mx-auto w-32 h-32 mb-8 relative"
                  >
                    <div className="absolute inset-0 bg-white/20 rounded-full animate-ping" />
                    <div className="relative w-full h-full bg-white rounded-full flex items-center justify-center">
                      <svg className="w-20 h-20 text-green-600" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                      </svg>
                    </div>
                  </motion.div>

                  <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.4 }}
                  >
                    <h2 className="text-4xl font-bold mb-4">
                      {walletActivationError ? '⚠️ Almost There!' : '🎉 Welcome Aboard!'}
                    </h2>
                    <p className="text-xl text-white/90 mb-6">
                      {walletActivationError 
                        ? 'Your registration is complete, but we need to activate your wallet.'
                        : 'Your account has been successfully created!'}
                    </p>
                    
                    {walletActivationError ? (
                      <div className="bg-white/20 backdrop-blur-sm rounded-2xl p-6 mb-6">
                        <p className="text-lg mb-3">Your wallet is not active yet.</p>
                        <p className="text-white/90 mb-4">Please contact our support team:</p>
                        <a 
                          href="tel:8130228919"
                          className="inline-flex items-center gap-2 text-2xl font-bold hover:underline"
                        >
                          <svg className="w-6 h-6" fill="currentColor" viewBox="0 0 20 20">
                            <path d="M2 3a1 1 0 011-1h2.153a1 1 0 01.986.836l.74 4.435a1 1 0 01-.54 1.06l-1.548.773a11.037 11.037 0 006.105 6.105l.774-1.548a1 1 0 011.059-.54l4.435.74a1 1 0 01.836.986V17a1 1 0 01-1 1h-2C7.82 18 2 12.18 2 5V3z" />
                          </svg>
                          8130228919
                        </a>
                      </div>
                    ) : (
                      <div className="bg-white/20 backdrop-blur-sm rounded-2xl p-6 mb-6">
                        <p className="text-lg">You're being redirected to your dashboard...</p>
                      </div>
                    )}

                    <div className="flex items-center justify-center gap-2">
                      <motion.div
                        animate={{ rotate: 360 }}
                        transition={{ duration: 1, repeat: Infinity, ease: "linear" }}
                      >
                        <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                        </svg>
                      </motion.div>
                      <span className="text-white/80">
                        Redirecting in 3 seconds...
                      </span>
                    </div>
                  </motion.div>
                </div>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default MerchantOnboarding;
